/**
 * Crochet hat patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const crochetHatPatternsPack: ToilePack = {
    slug: 'crochet-hat-patterns',
    category: 'craft',
    name: 'Crochet hat patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'beanie', label: 'Beanie', subject: 'a pattern sheet for a ribbed beanie, a crown diagram of increase rounds from a magic ring, the body drawn flat as a rectangle of ribbing, and a folded brim at the bottom'},
        {key: 'bucket', label: 'Bucket hat', subject: 'a pattern sheet for a crochet bucket hat, a flat circle crown diagram, the straight sides and the widening brim drawn as a side profile with each section outlined'},
        {key: 'baby', label: 'Baby hat', subject: 'a pattern sheet for a baby hat with two small ears on top, a crown diagram in rounds, the ears drawn as small half circles, and the hat shown flat from the front'},
    ],
    styles: [
        {key: 'schematic', label: 'Schematic', style: 'technical crown diagram of concentric increase rounds beside a flat side view, thin black outlines and dashed fold lines', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'sketch', label: 'Fashion flat', style: 'clean sketch of the finished hat beside its crown diagram, fine stitch texture lines on the hat', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'rounds', label: 'Round colors', style: 'crown diagram with every increase round on its own pale colour ring, outlines and symbols in black', palette: 'black outlines and pale colour rings on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['kids size', 'with ears', 'slouchy'],
    directionHint: 'try: kids size',
    seo: {
        title: 'Crochet hat patterns: beanie, bucket hat, baby hat',
        metaDescription: 'Nine crochet hat pattern sheets: a beanie, a bucket hat and a baby hat, with crown diagrams and flat side views.',
        h1: 'Crochet hat patterns: a beanie, a bucket hat and a baby hat',
        tagline: 'Crown diagrams worked from the centre out, beside a flat side view of the finished hat.',
        intro: 'Hats are where most crocheters learn to shape in the round, and the crown increases decide the fit. These nine cover a ribbed beanie, a bucket hat and a baby hat. Each sheet pairs a crown diagram, worked from the centre out, with a flat side view. Type a different size or shape and the same pairing carries it.',
        useCases: [
            'a first project in the round',
            'a baby hat for a gift',
            'a bucket hat for summer',
            'a class on increases',
        ],
        faq: [
            {q: 'What does the crown diagram show?', a: 'Each ring is one round, drawn from the centre out, so you can see where the increases fall.'},
            {q: 'Is this a full pattern?', a: 'No. It shows the shape and where the increases fall. It is a sheet to work from, not a written pattern.'},
            {q: 'Which style helps me count rounds?', a: 'The round colours style puts each increase round on its own pale ring, so you can count them at a glance.'},
        ],
    },
}
